import { Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import {
  EventsService,
  GroupsService,
  InvitableUserDto,
  SearchInvitableUsersRequestParams,
  EventsSearchInvitableUsersRequestParams,
  GroupsControllerSearchInvitableUsers200Response,
  EventsSearchInvitableUsers200Response,
} from '@smatchy/sdk';
import { Observable, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import {
  SearchInvitableUsersAction,
  InviteUserToGroupAction,
  InviteMultipleUsersToGroupAction,
  ClearInviteUsersStateAction,
  MarkUserAsInvitedAction,
  RemovePendingUserAction,
  RemoveInvitableUserAction,
  UpdateInvitableUserStatusAction,
  SetInitialPendingUsersAction,
} from './invite-users.actions';
import { ShowToastErrorAction } from '../../app';
import { TranslateService } from '@ngx-translate/core';
import { settleInSmallWaves } from './invitations-leave-in-small-waves';

type InvitableSearchResponse = GroupsControllerSearchInvitableUsers200Response | EventsSearchInvitableUsers200Response;

export interface InviteUsersStateModel {
  users: InvitableUserDto[];
  invitedUserIds: string[];
  pendingUserIds: string[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  invitingUserId: string | null;
  invitingAll: boolean;
  total: number;
  loadingAll: boolean;
}

const defaults: InviteUsersStateModel = {
  users: [],
  invitedUserIds: [],
  pendingUserIds: [],
  loading: false,
  loadingMore: false,
  hasMore: true,
  invitingUserId: null,
  invitingAll: false,
  total: 0,
  loadingAll: false,
};

@State<InviteUsersStateModel>({
  name: 'inviteUsers',
  defaults,
})
@Injectable()
export class InviteUsersState {
  constructor(
    protected groupsService: GroupsService,
    protected eventsService: EventsService,
    private readonly translate: TranslateService,
  ) {}

  @Action(SearchInvitableUsersAction)
  searchInvitableUsers(
    { patchState, getState, dispatch }: StateContext<InviteUsersStateModel>,
    {
      targetId,
      query,
      limit,
      append,
      location,
      radiusMeter,
      status,
      targetType,
      sportIds,
      fetchAll,
      searchMode,
      hierarchy,
    }: SearchInvitableUsersAction,
  ) {
    if (fetchAll) {
      patchState({ loadingAll: true });
    } else if (append) {
      patchState({ loadingMore: true });
    } else {
      patchState({ loading: true, hasMore: true });
    }
    const requestParams = this.buildInvitableSearchParams({
      targetId,
      query,
      limit,
      location,
      radiusMeter,
      status,
      sportIds,
      fetchAll,
      searchMode,
      hierarchy,
    });
    const search$: Observable<InvitableSearchResponse> =
      targetType === 'event'
        ? this.eventsService.eventsSearchInvitableUsers(requestParams)
        : this.groupsService.searchInvitableUsers(requestParams);
    const ctx = { patchState, getState, dispatch };
    return search$.pipe(
      tap((res: InvitableSearchResponse) => {
        this.applyInvitableSearchResponse(ctx, res, {
          append,
          fetchAll,
          limit,
        });
      }),
      catchError(() => {
        const currentState = getState();
        patchState({
          loading: false,
          loadingMore: false,
          loadingAll: false,
          hasMore: false,
          users: append ? currentState.users : [],
        });
        return of(null);
      }),
    );
  }

  private applyInvitableSearchResponse(
    ctx: Pick<StateContext<InviteUsersStateModel>, 'patchState' | 'getState' | 'dispatch'>,
    res: InvitableSearchResponse,
    options: { append: boolean; fetchAll: boolean; limit: number },
  ): void {
    const { patchState, getState, dispatch } = ctx;
    if (res.error || !res.value) {
      if (res.error === 'event_expired') {
        dispatch(new ShowToastErrorAction(this.translate.instant('ACTIVITY_DETAIL_PAGE.EVENT_EXPIRED')));
      }
      patchState({
        loading: false,
        loadingMore: false,
        loadingAll: false,
        hasMore: false,
        users: options.append ? getState().users : [],
      });
      return;
    }
    const newUsers = (res.value.users || []) as InvitableUserDto[];
    const hasMoreUsers = newUsers.length >= options.limit;
    const total = res.value.total || 0;
    const pendingUserIds = newUsers
      .filter((user) => {
        const withStatus = user as InvitableUserDto & {
          isPending?: boolean;
          status?: string;
        };
        return withStatus.isPending === true || withStatus.status === 'pending';
      })
      .map((user) => user.userId);
    if (options.fetchAll) {
      patchState({
        loadingAll: false,
        hasMore: false,
        users: newUsers,
        pendingUserIds,
        total,
      });
      return;
    }
    if (options.append) {
      const currentState = getState();
      const currentUserIds = new Set(currentState.users.map((user) => user.userId));
      const uniqueNewUsers = newUsers.filter((user) => !currentUserIds.has(user.userId));
      patchState({
        loadingMore: false,
        hasMore: hasMoreUsers && uniqueNewUsers.length > 0,
        users: [...currentState.users, ...uniqueNewUsers],
        pendingUserIds: [...new Set([...currentState.pendingUserIds, ...pendingUserIds])],
        total,
      });
      return;
    }
    patchState({
      loading: false,
      hasMore: hasMoreUsers,
      users: newUsers,
      pendingUserIds,
      total,
    });
  }

  private buildInvitableSearchParams(input: {
    targetId: string;
    query: string;
    limit: number;
    location?: { lat: number; lon: number };
    radiusMeter?: number;
    status?: string;
    sportIds?: string[];
    fetchAll?: boolean;
    searchMode?: 'radius' | 'hierarchy';
    hierarchy?: SearchInvitableUsersAction['hierarchy'];
  }): SearchInvitableUsersRequestParams & EventsSearchInvitableUsersRequestParams {
    const params: SearchInvitableUsersRequestParams & EventsSearchInvitableUsersRequestParams = {
      id: input.targetId,
      query: input.query?.trim() || '',
      limit: input.limit,
    };
    if (input.fetchAll) {
      params.fetchAll = true;
    }
    if (input.location?.lat !== undefined && input.location?.lon !== undefined) {
      params.lat = input.location.lat;
      params.lon = input.location.lon;
    }
    if (input.status) {
      params.status = input.status;
    }
    if (input.sportIds?.length) {
      params.sportIds = input.sportIds.join(',');
    }
    const isHierarchySearch = input.searchMode === 'hierarchy' || !!input.hierarchy?.level;
    if (isHierarchySearch && input.hierarchy?.level) {
      params.searchMode = 'hierarchy';
      params.hierarchyLevel = input.hierarchy.level;
      if (input.hierarchy.placeId) {
        params.hierarchyPlaceId = input.hierarchy.placeId;
      }
      if (input.hierarchy.name) {
        params.hierarchyName = input.hierarchy.name;
      }
      if (input.hierarchy.countryCode) {
        params.hierarchyCountryCode = input.hierarchy.countryCode;
      }
      return params;
    }
    if (input.radiusMeter !== undefined && input.radiusMeter !== null && input.radiusMeter > 0) {
      params.radiusMeter = input.radiusMeter;
      params.searchMode = 'radius';
    }
    return params;
  }

  @Action(InviteUserToGroupAction)
  inviteUserToGroup(
    { patchState, dispatch }: StateContext<InviteUsersStateModel>,
    { targetId, userId, targetType }: InviteUserToGroupAction,
  ) {
    patchState({ invitingUserId: userId });

    const invite$ =
      targetType === 'event'
        ? this.eventsService.inviteUserToJoinEvent({ id: targetId, userId })
        : this.groupsService.inviteUserToJoinGroup({ id: targetId, userId });

    return invite$.pipe(
      tap((res) => {
        patchState({ invitingUserId: null });
        if (res.error === 'event_expired') {
          dispatch(new ShowToastErrorAction(this.translate.instant('ACTIVITY_DETAIL_PAGE.EVENT_EXPIRED')));
          return;
        }
        if (!res.error) {
          dispatch(new MarkUserAsInvitedAction(userId));
        }
      }),
      catchError(() => {
        patchState({ invitingUserId: null });
        return of(null);
      }),
    );
  }

  @Action(InviteMultipleUsersToGroupAction)
  async inviteMultipleUsersToGroup(
    { patchState, dispatch }: StateContext<InviteUsersStateModel>,
    { targetId, userIds, targetType }: InviteMultipleUsersToGroupAction,
  ) {
    patchState({ invitingAll: true });

    const results = await settleInSmallWaves(userIds, (userId) =>
      (targetType === 'event'
        ? this.eventsService.inviteUserToJoinEvent({ id: targetId, userId })
        : this.groupsService.inviteUserToJoinGroup({ id: targetId, userId })
      ).toPromise(),
    );

    const successfullyInvitedIds: string[] = [];

    results.forEach((result, index) => {
      if (result.status === 'fulfilled' && !result.value?.error) {
        successfullyInvitedIds.push(userIds[index]);
        dispatch(new MarkUserAsInvitedAction(userIds[index]));
      }
    });

    if (results.some((result) => result.status === 'fulfilled' && result.value?.error === 'event_expired')) {
      dispatch(new ShowToastErrorAction(this.translate.instant('ACTIVITY_DETAIL_PAGE.EVENT_EXPIRED')));
    }

    patchState({ invitingAll: false });

    return successfullyInvitedIds.length;
  }

  @Action(MarkUserAsInvitedAction)
  markUserAsInvited(
    { patchState, getState }: StateContext<InviteUsersStateModel>,
    { userId }: MarkUserAsInvitedAction,
  ) {
    const state = getState();
    const updatedUsers = state.users.map((user) => {
      if (user.userId === userId) {
        return {
          ...user,
          status: InvitableUserDto.StatusEnum.Pending,
          isPending: true,
        } as InvitableUserDto;
      }
      return user;
    });

    patchState({
      invitedUserIds: [...state.invitedUserIds, userId],
      pendingUserIds: [...state.pendingUserIds, userId],
      users: updatedUsers,
    });
  }

  @Action(RemovePendingUserAction)
  removePendingUser(
    { patchState, getState }: StateContext<InviteUsersStateModel>,
    { userId }: RemovePendingUserAction,
  ) {
    const state = getState();
    patchState({
      pendingUserIds: state.pendingUserIds.filter((id) => id !== userId),
      invitedUserIds: state.invitedUserIds.filter((id) => id !== userId),
    });
  }

  @Action(RemoveInvitableUserAction)
  removeInvitableUser(
    { patchState, getState }: StateContext<InviteUsersStateModel>,
    { userId }: RemoveInvitableUserAction,
  ) {
    const state = getState();
    patchState({
      users: state.users.filter((u) => u.userId !== userId),
      pendingUserIds: state.pendingUserIds.filter((id) => id !== userId),
      invitedUserIds: state.invitedUserIds.filter((id) => id !== userId),
    });
  }

  @Action(UpdateInvitableUserStatusAction)
  updateInvitableUserStatus(
    { patchState, getState }: StateContext<InviteUsersStateModel>,
    { userId, status, messageInvite }: UpdateInvitableUserStatusAction,
  ) {
    const state = getState();

    const updatedUsers = state.users.map((user) => {
      if (user.userId !== userId) return user;

      return {
        ...user,
        status,
        isPending: status === InvitableUserDto.StatusEnum.Pending,
        messageInvite,
      } as InvitableUserDto;
    });

    patchState({
      users: updatedUsers,
      pendingUserIds:
        status === InvitableUserDto.StatusEnum.Pending
          ? Array.from(new Set([...state.pendingUserIds, userId]))
          : state.pendingUserIds.filter((id) => id !== userId),
      invitedUserIds:
        status === InvitableUserDto.StatusEnum.Pending
          ? Array.from(new Set([...state.invitedUserIds, userId]))
          : state.invitedUserIds.filter((id) => id !== userId),
    });
  }

  @Action(ClearInviteUsersStateAction)
  clearState({ patchState }: StateContext<InviteUsersStateModel>) {
    patchState(defaults);
  }

  @Action(SetInitialPendingUsersAction)
  setInitialPendingUsers(
    { patchState, getState }: StateContext<InviteUsersStateModel>,
    { userIds }: SetInitialPendingUsersAction,
  ) {
    const state = getState();
    // Merge with existing pending user IDs to avoid duplicates
    const mergedPendingIds = [...new Set([...state.pendingUserIds, ...userIds])];
    patchState({
      pendingUserIds: mergedPendingIds,
    });
  }
}
